import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  ButtonV2,
  LabraToolTipV2,
  ModalConfirmV2,
  ModalSizeVariants,
  PlanType,
} from '@labrav/react-components'
import { useStyles } from './ChannelOfferCreationModal.styles'
import { faListCheck } from '@fortawesome/pro-regular-svg-icons'
import { Divider } from '@mui/material'
import {
  DetailInfo,
  DetailRow,
} from '../../../common/components/DetailRow/DetailRow'
import { useSelector } from '../../../store'
import type { ReceivedResellerAuthorization } from '../../../modules/receivedResellerAuthorization/types'
import { AuthorizationSelector } from '../AuthorizationSelector'
import {
  agreementDurationTypeViewMapping,
  discountTypeViewMapping,
} from '../../ResellerAuthorizations/ViewDetailsResellerAuthorization/ResellerAuthorizationDetails/constants'
import { getContractDurationForEndCustomer } from '../../ResellerAuthorizations/ViewDetailsResellerAuthorization/ResellerAuthorizationDetails/helpers'
import { noop } from 'lodash'
import { getSelectedReceivedResellerAuth, ValueComp } from './helpers'
import type { ChannelOfferCreationModalProps } from './types'

export type { ChannelOfferCreationModalProps } from './types'

export const ChannelOfferCreationModal = ({
  open,
  setOpen,
  handleNext,
  subscriptionId,
}: ChannelOfferCreationModalProps) => {
  const { classes } = useStyles()
  const selectedActiveReceivedResellerAuthorization = useSelector<string>(
    state =>
      state?.receivedResellerAuthorization
        ?.selectedActiveReceivedResellerAuthorization
  )

  const activeReceivedResellerAuthorizations = useSelector<
    ReceivedResellerAuthorization[]
  >(
    state =>
      state?.receivedResellerAuthorization?.activeReceivedResellerAuthorizations
  )

  const selectedReceivedResellerAuth = getSelectedReceivedResellerAuth(
    activeReceivedResellerAuthorizations,
    selectedActiveReceivedResellerAuthorization
  )

  return (
    <>
      <ModalConfirmV2
        open={!!open}
        title={'Select authorization'}
        icon={<FontAwesomeIcon style={{ height: '16px' }} icon={faListCheck} />}
        description={
          'Select a Reseller authorization to create a Channel offer for your buyer.'
        }
        onDecline={() => setOpen('')}
        hideAcceptButton
        hideCancelButton
        onAccept={noop}
        variant={ModalSizeVariants.large}
        content={
          <div className={classes.root}>
            <div className={classes.selector}>
              <AuthorizationSelector subscriptionId={subscriptionId} />
            </div>
            {selectedActiveReceivedResellerAuthorization && (
              <>
                <Divider className={classes.divider} />

                <div>
                  <div className={classes.heading}>
                    Reseller authorization details
                  </div>
                  <div className={classes.detail}>
                    <DetailRow
                      className={classes.row}
                      children={
                        <>
                          <DetailInfo
                            title={'ISV name'}
                            value={
                              <ValueComp
                                value={
                                  selectedReceivedResellerAuth?.isvName || '-'
                                }
                              />
                            }
                          />
                          <DetailInfo
                            title={'Authorization time length'}
                            value={
                              <ValueComp
                                value={
                                  selectedReceivedResellerAuth?.agreementDurationType
                                    ? agreementDurationTypeViewMapping[
                                        selectedReceivedResellerAuth?.agreementDurationType?.toLowerCase()
                                      ]
                                    : '-'
                                }
                              />
                            }
                          />
                        </>
                      }
                    />
                    <DetailRow
                      className={classes.row}
                      children={
                        <>
                          <DetailInfo
                            title={'ISV AWS account ID'}
                            value={
                              <ValueComp
                                value={
                                  selectedReceivedResellerAuth?.isvCloudAccountId ||
                                  '-'
                                }
                              />
                            }
                          />
                          <DetailInfo
                            title={'Product'}
                            value={
                              <ValueComp
                                value={
                                  selectedReceivedResellerAuth?.cloudProductName ||
                                  '-'
                                }
                              />
                            }
                          />
                        </>
                      }
                    />
                    <DetailRow
                      className={classes.row}
                      children={
                        <>
                          <DetailInfo
                            title={'Discount type set by ISV'}
                            value={
                              <ValueComp
                                value={
                                  selectedReceivedResellerAuth?.discountType
                                    ? discountTypeViewMapping[
                                        selectedReceivedResellerAuth?.discountType?.toUpperCase()
                                      ]
                                    : '-'
                                }
                              />
                            }
                          />
                          {selectedReceivedResellerAuth?.productType !==
                            PlanType?.SAAS_SUBSCRIPTION && (
                            <DetailInfo
                              title={'Contract duration'}
                              value={
                                <ValueComp
                                  value={
                                    selectedReceivedResellerAuth
                                      ? getContractDurationForEndCustomer(
                                          selectedReceivedResellerAuth,
                                          true
                                        ) || '-'
                                      : '-'
                                  }
                                />
                              }
                            />
                          )}
                        </>
                      }
                    />
                  </div>
                </div>
              </>
            )}
            <div className={classes.rowRightItem}>
              <ButtonV2
                data-testid="send-reminder"
                smallVariant
                styleType="outlined"
                onClick={() => setOpen('')}
              >
                Back
              </ButtonV2>
              <LabraToolTipV2
                tooltipPlacement="bottom"
                description={
                  !selectedActiveReceivedResellerAuthorization
                    ? 'Select one authorization to create a channel offer.'
                    : ''
                }
              >
                <ButtonV2
                  data-testid="send-reminder"
                  smallVariant
                  styleType="primary"
                  onClick={() => handleNext(subscriptionId)}
                  disabled={!selectedActiveReceivedResellerAuthorization}
                >
                  Next
                </ButtonV2>
              </LabraToolTipV2>
            </div>
          </div>
        }
      />
    </>
  )
}
